import { FREE_DOMAINS } from 'configs/constants';
import { parsePhoneNumberFromString, CountryCode } from 'libphonenumber-js';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { DATE_FORMAT } from 'configs/constants';
dayjs.extend(utc);

export const getNA = <T>(value?: T): T | 'N/A' => {
  return value ?? 'N/A';
};

export const getInitials = (name: string | null) => {
  if (!name) return getNA(name);
  return name
    .split(' ')
    .map((word) => word[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
};

export const phoneValidator = (value: string, countryIso: CountryCode, t: (key: string) => string) => {
  if (!value) {
    return Promise.resolve();
  }

  if (value.includes('+')) {
    return Promise.reject(new Error(t('auth.signup.rules.phone_invalid')));
  }

  const phoneNumber = parsePhoneNumberFromString(value, countryIso);

  if (phoneNumber?.isValid()) {
    return Promise.resolve();
  } else {
    return Promise.reject(new Error(t('auth.signup.rules.phone_invalid')));
  }
};

export const validatePublicEmail = (value: string, t: (key: string) => string) => {
  if (!value) {
    return Promise.resolve();
  }
  const emailDomain = value.split('@')[1]?.toLowerCase();

  if (FREE_DOMAINS.includes(emailDomain)) {
    return Promise.reject(new Error(t('auth.signup.rules.corporate_email_required')));
  }

  return Promise.resolve();
};

interface HandleCountryChangeParams {
  value: string;
  data?: any;
  setSelectedCountry: React.Dispatch<React.SetStateAction<{ code: CountryCode; iso: string | undefined }>>;
  form: { setFieldsValue: (fields: Record<string, unknown>) => void };
}

export const onCountryChange = ({ value, data, setSelectedCountry, form }: HandleCountryChangeParams) => {
  const [countryCode, countryIso] = value.split(' ');
  const selectedCountry = data?.data?.find(
    (country: { phoneCountryCode: string; isoAlpha2: string }) =>
      country.phoneCountryCode === countryCode && country.isoAlpha2 === countryIso,
  );
  setSelectedCountry({ code: selectedCountry.phoneCountryCode as CountryCode, iso: selectedCountry.isoAlpha2 });
  form.setFieldsValue({ contactNumber: '' });
};

export const cleanMarkdownContent = (content: string) => {
  let cleanedContent = content.trim();
  cleanedContent = cleanedContent.replace(/\n\s*\n/g, '\n\n');
  cleanedContent = cleanedContent
    .split('\n')
    .map((line: string) => line.trim())
    .join('\n');
  return cleanedContent;
};

interface TableFilters {
  id?: number;
  name?: string;
  leadTraveler?: string;
  agentId?: string | number;
  [key: string]: string | number | boolean | null | undefined;
}

interface TableSort {
  [key: string]: 'asc' | 'desc';
}

export interface TableParams {
  page: number;
  size: number;
  filters?: TableFilters;
  sort?: TableSort;
}

export const buildTableParams = (
  searchParams: URLSearchParams,
  filters: TableFilters,
  sort: TableSort,
  isInvoiceTripParams?: boolean,
): TableParams => {
  let page = Number(searchParams.get(isInvoiceTripParams ? 'tripPageNumber' : 'page')) || 0;
  let size = Number(searchParams.get(isInvoiceTripParams ? 'tripPageSize' : 'size')) || 10;

  const params: TableParams = {
    page,
    size,
  };

  if (Object.keys(filters).length > 0) {
    params.filters = filters;
  }

  if (Object.keys(sort).length > 0) {
    params.sort = sort;
  }

  return params;
};

export const buildAssignedTableParams = (
  searchParams: URLSearchParams,
  filters: TableFilters,
  sort: TableSort,
): TableParams => {
  let page = Number(searchParams.get('page')) || 0;
  let size = Number(searchParams.get('size')) || 10;

  // If filters are present, reset page and size to defaults
  if (filters.id || filters.name || filters.leadTraveler) {
    page = 0;
    size = 10;
  }

  const params: TableParams = {
    page,
    size,
  };

  if (filters.id || filters.name || filters.leadTraveler) {
    params.filters = filters;
  } else {
    params.filters = {
      agentId: parseInt(filters.agentId as string),
      includeAgentId: true,
    };
  }

  if (Object.keys(sort).length > 0) {
    params.sort = sort;
  }

  return params;
};

/**
 * Ensures a URL has the proper protocol
 * @param url - The URL to process
 * @returns URL with proper protocol
 */
export const ensureUrlProtocol = (url: string): string => {
  if (!url) return '';

  // If URL already has protocol, return as is
  if (url.startsWith('http://') || url.startsWith('https://')) {
    return url;
  }

  // If URL starts with www., add https://
  if (url.startsWith('www.')) {
    return `https://${url}`;
  }

  // For other cases, add https://
  return `https://${url}`;
};

export const formatTimeAgo = (dateStr: string, t: (key: string) => string) => {
  const now = dayjs();
  const localDate = dayjs.utc(dateStr).local();
  const diffInMinutes = now.diff(localDate, 'minute');

  // Debugging logs
  console.log('[formatTimeAgo] Input:', dateStr);
  console.log('[formatTimeAgo] Parsed localDate:', localDate.format());
  console.log('[formatTimeAgo] Now:', now.format());
  console.log('[formatTimeAgo] Diff in minutes:', diffInMinutes);

  if (diffInMinutes === 0) {
    return t('notifications.now');
  }
  if (diffInMinutes < 60) {
    return `${diffInMinutes}m`;
  }
  const hours = Math.floor(diffInMinutes / 60);
  const minutes = diffInMinutes % 60;
  if (minutes === 0) {
    return `${hours}h`;
  }
  return `${hours}h ${minutes}m`;
};

export const formatNotificationDate = (createdAt: string, t: (key: string) => string) => {
  const now = dayjs();
  const localDate = dayjs.utc(createdAt).local();
  const diffInHours = now.diff(localDate, 'hour', true);
  if (diffInHours < 24) {
    return formatTimeAgo(createdAt, t);
  }
  return localDate.format(DATE_FORMAT);
};
